// MaturaArcLengthClosedTask.js
// Dział 6 – planimetria
// Podtemat 2 – długość łuku (zadanie zamknięte)
// Wersja: trójkąt prostokątny na średnicy, liczba dane: promień i ostry kąt.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import ArcLengthRightTriangleDiagram from "../../../../components/diagrams/ArcLengthRightTriangleDiagram";
import MaturaCircleTask21Diagram from "../../../../components/diagrams/MaturaCircleTask21Diagram";

const LETTERS = ["A", "B", "C", "D"];

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function createOriginalTask() {
  const values = [8, 10, 13, 20];
  const options = values.map((value, index) => ({
    value,
    latex: `${value} \\pi`,
    label: LETTERS[index],
    isCorrect: index === 1,
  }));

  return {
    type: "MaturaArcLengthClosedTask",
    data: {
      r: 36,
      alpha_w: 25,
      alpha_sr: 50,
      gamma: 65,
      m: 10,
      options,
      correctIndex: 1,
      originalSharedDiagram: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "21.2",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  const possibleR = [12, 15, 18, 20, 24, 30, 36];

  let r;
  let alpha_sr; // kąt środkowy nad łukiem AC (całkowity, parzysty)
  let alpha_w; // kąt ostry przy B (na łuku) – całkowity
  let gamma; // dany kąt ostry przy A – całkowity
  let m; // tak, że długość łuku = m·π

  // dobieramy dane tak, żeby:
  // - m było ładną liczbą całkowitą,
  // - kąt środkowy był całkowity i parzysty (=> wszystkie kąty w trójkącie całkowite)
  while (true) {
    r = pick(possibleR);
    const candidatesM = [4, 5, 6, 8, 9, 10, 12, 14, 15, 16, 18, 20];
    m = pick(candidatesM);
    const alphaCandidate = (180 * m) / r; // kąt środkowy

    if (
      Number.isInteger(alphaCandidate) && // całkowity
      alphaCandidate % 2 === 0 && // parzysty => alpha_w całkowity
      alphaCandidate >= 20 &&
      alphaCandidate <= 160 &&
      alphaCandidate !== 90 // wtedy oba kąty ostre miałyby po 45°
    ) {
      alpha_sr = alphaCandidate;
      break;
    }
  }

  // kąt przy B (na łuku) – wpisany
  alpha_w = alpha_sr / 2; // całkowity, bo alpha_sr parzysty
  // dany kąt przy A – drugi ostry w trójkącie prostokątnym
  gamma = 90 - alpha_w; // też całkowity

  const correctValue = m;

  // błędne odpowiedzi – typowe pomyłki
  const wrong1 = m + 2;
  const wrong2 = Math.max(1, m - 2);
  const wrong3 = 2 * m;

  const raw = [
    { value: correctValue, isCorrect: true },
    { value: wrong1, isCorrect: false },
    { value: wrong2, isCorrect: false },
    { value: wrong3, isCorrect: false },
  ];

  const seen = new Set();
  const unique = [];
  raw.forEach((opt) => {
    let v = opt.value;
    while (seen.has(v)) v += 1; // unikamy duplikatów
    seen.add(v);
    unique.push({ ...opt, value: v });
  });

  const shuffled = shuffle(unique).map((opt, idx) => ({
    label: LETTERS[idx],
    value: opt.value,
    latex: `${opt.value} \\pi`,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaArcLengthClosedTask",
    data: {
      r,
      alpha_w,
      alpha_sr,
      gamma,
      m: correctValue,
      options: shuffled,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { r, gamma, options } = data;

  if (data.originalSharedDiagram) {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          Punkty <InlineMath math={"A, B, C, D"} /> leżą na okręgu o środku
          w punkcie <InlineMath math={"S"} /> i promieniu{" "}
          <InlineMath math={"36"} />. Punkt <InlineMath math={"S"} /> leży na
          odcinku <InlineMath math={"BD"} />. Kąt{" "}
          <InlineMath math={"BDA"} /> ma miarę{" "}
          <InlineMath math={"40^\\circ"} />, a kąt{" "}
          <InlineMath math={"DBC"} /> ma miarę{" "}
          <InlineMath math={"65^\\circ"} />.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <MaturaCircleTask21Diagram />
        </div>
        <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
        <p>
          Długość łuku <InlineMath math={"BC"} />, na którym jest oparty kąt
          wpisany <InlineMath math={"CDB"} />, jest równa
        </p>
        <div>
          {options.map((option) => (
            <div key={option.label}>
              <InlineMath math={`${option.label}.\\;${option.latex}`} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Punkty <InlineMath math={'A, B, C'} /> leżą na okręgu o środku w
        punkcie <InlineMath math={'S'} /> i promieniu{" "}
        <InlineMath math={`r = ${r}`} />. Odcinek{" "}
        <InlineMath math={'AB'} /> jest średnicą tego okręgu.
      </p>
      <p>
        Na rysunku przedstawiono trójkąt{" "}
        <InlineMath math={'\\triangle ABC'} /> wpisany w ten okrąg. Miara kąta
        przy wierzchołku <InlineMath math={'A'} /> jest równa{" "}
        <InlineMath math={`${gamma}^{\\circ}`} />.
      </p>

      <div
        style={{
          marginTop: 8,
          marginBottom: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <ArcLengthRightTriangleDiagram
          givenAngleDeg={gamma}
          showCentral={false} // w treści NIE pokazujemy kąta środkowego
          radiusLabel={String(r)}
        />
      </div>

      <p>
        Długość łuku <InlineMath math={'AC'} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];

  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  if (data.originalSharedDiagram) {
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          <strong>Krok 1.</strong> Odcinek <InlineMath math={"BD"} /> jest
          średnicą, więc kąt <InlineMath math={"BCD"} /> jest prosty.
        </p>
        <BlockMath math={"\\angle CDB=180^\\circ-90^\\circ-65^\\circ=25^\\circ"} />
        <p>
          <strong>Krok 2.</strong> Kąt środkowy oparty na łuku{" "}
          <InlineMath math={"BC"} /> ma miarę
        </p>
        <BlockMath math={"\\angle BSC=2\\cdot25^\\circ=50^\\circ"} />
        <p>
          <strong>Krok 3.</strong> Obliczamy odpowiednią część obwodu okręgu:
        </p>
        <BlockMath math={"l=\\dfrac{50^\\circ}{360^\\circ}\\cdot2\\pi\\cdot36=10\\pi"} />
        <p>Poprawna jest odpowiedź <InlineMath math="B" />.</p>
      </div>
    );
  }

  const { r, alpha_sr, alpha_w, gamma, m } = data;

  const beta = alpha_w; // ostry przy B

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Trójkąt <InlineMath math={'ABC'} /> jest wpisany w okrąg, a{" "}
        <InlineMath math={'AB'} /> jest średnicą. Stąd kąt przy wierzchołku{" "}
        <InlineMath math={'C'} /> jest prosty.
      </p>

      <p>
        <strong>Krok 1.</strong> Obliczamy drugi ostry kąt trójkąta.
      </p>
      <BlockMath math={'90^{\\circ} + \\gamma + \\beta = 180^{\\circ}'} />
      <BlockMath
        math={`\\beta = 180^{\\circ} - 90^{\\circ} - ${gamma}^{\\circ} = ${beta}^{\\circ}`}
      />

      <p>
        <strong>Krok 2.</strong> Kąt przy wierzchołku{" "}
        <InlineMath math={'B'} /> jest kątem wpisanym opartym na łuku{" "}
        <InlineMath math={'AC'} />. Kąt środkowy oparty na tym samym łuku jest
        dwa razy większy:
      </p>
      <BlockMath math={'\\alpha_{\\text{sr}} = 2\\beta'} />
      <BlockMath
        math={`\\alpha_{\\text{sr}} = 2 \\cdot ${beta}^{\\circ} = ${alpha_sr}^{\\circ}`}
      />

      <p>
        <strong>Krok 3.</strong> Korzystamy ze wzoru na długość łuku okręgu:
      </p>
      <BlockMath math={'l = \\frac{\\alpha_{\\text{sr}}}{360^{\\circ}} \\cdot 2\\pi r'} />
      <BlockMath
        math={`l = \\frac{${alpha_sr}^{\\circ}}{360^{\\circ}} \\cdot 2\\pi \\cdot ${r}`}
      />
      <BlockMath
        math={`l = \\frac{${alpha_sr} \\cdot ${r}}{180} \\pi = ${m} \\pi`}
      />

      <div
        style={{
          marginTop: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <ArcLengthRightTriangleDiagram
          givenAngleDeg={gamma}
          showCentral={true}
          centralAngleDeg={alpha_sr}
          radiusLabel={String(r)}
          showBetaAtB={true}
          betaLabel={`${beta}°`}
        />
      </div>

      <p style={{ marginTop: 8 }}>
        Dlatego długość łuku <InlineMath math={'AC'} /> jest równa{" "}
        <InlineMath math={`${m} \\pi`} />.
      </p>
    </div>
  );
}
